import { memo } from 'react';
import { Box, Flex, Text, Badge, Progress, Heading } from '@chakra-ui/react';
import { formatDisableDate } from 'utils/utils';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS
   ───────────────────────────────────────────────────────────── */
const INK      = '#1A2740';
const MUTED    = '#5A6B7E';
const DANGER   = '#E53E3E';
const WARNING  = '#EA580C';
const WARNING_BG = '#FFF7ED';
const FONT = "'Bricolage Grotesque', sans-serif";

const ClockIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

const AlertIcon = (props) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...props}>
        <path
            d="M12 2L2 22h20L12 2zm0 6v6m0 2v2"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const TimeUnit = ({ value, label }) => (
    <Box textAlign="center" minW="38px">
        <Text fontSize="22px" fontWeight="800" color={DANGER} fontFamily={FONT} lineHeight="1">
            {String(value).padStart(2, '0')}
        </Text>
        <Text fontSize="9px" color={MUTED} fontWeight="700" letterSpacing="0.05em" mt="2px">
            {label}
        </Text>
    </Box>
);

const Separator = () => (
    <Text fontSize="16px" fontWeight="800" color={DANGER} alignSelf="center">
        :
    </Text>
);

/**
 * LimitedOfferAlert — banner shown above the product detail when there's
 * a disable date in the future. Displays the deadline and a live countdown.
 */
const LimitedOfferAlert = memo(function LimitedOfferAlert({ countdown, disableDate }) {
    const formatted = formatDisableDate(disableDate)?.formatted ?? '';
    const showDays = countdown.days > 0;
    const progress = Math.min(100, Math.max(0, (1 - countdown.days / 30) * 100));

    return (
        <Box
            mb="20px"
            bg={WARNING_BG}
            borderRadius="12px"
            borderLeft="4px solid"
            borderColor={DANGER}
            p={{ base: '14px', md: '18px' }}
            fontFamily={FONT}
        >
            <Flex
                justify="space-between"
                align={{ base: 'flex-start', md: 'center' }}
                direction={{ base: 'column', md: 'row' }}
                gap="14px"
            >
                {/* Left — message */}
                <Flex align="flex-start" gap="10px" flex="1">
                    <Box color={DANGER} mt="2px" flexShrink={0}>
                        <AlertIcon />
                    </Box>
                    <Box>
                        <Heading
                            as="h3"
                            fontSize="14px"
                            color={DANGER}
                            fontWeight="800"
                            fontFamily={FONT}
                            mb="2px"
                        >
                            Limited time offer
                        </Heading>
                        <Text fontSize="12px" color={INK} lineHeight="1.5">
                            Unavailable after{' '}
                            <Text as="span" fontWeight="700">{formatted}</Text>
                        </Text>
                    </Box>
                </Flex>

                {/* Right — countdown */}
                <Box
                    bg="white"
                    borderRadius="10px"
                    border="1px solid"
                    borderColor={`${DANGER}30`}
                    px="14px"
                    py="12px"
                    minW={{ base: 'full', md: '320px' }}
                >
                    <Flex justify="space-between" align="center" mb="8px">
                        <Flex align="center" gap="6px">
                            <Box color={DANGER}><ClockIcon /></Box>
                            <Text fontSize="11px" fontWeight="700" color={MUTED}>
                                Time remaining
                            </Text>
                        </Flex>
                        <Badge
                            bg={`${DANGER}15`}
                            color={DANGER}
                            fontSize="9px"
                            fontWeight="800"
                            px="6px"
                            py="2px"
                            borderRadius="full"
                            letterSpacing="0.04em"
                        >
                            {showDays
                                ? `${countdown.days} ${countdown.days === 1 ? 'DAY' : 'DAYS'}`
                                : 'LESS THAN A DAY'}
                        </Badge>
                    </Flex>

                    <Flex justify="center" gap="6px" mb="6px">
                        {showDays && (
                            <>
                                <TimeUnit value={countdown.days} label="DAYS" />
                                <Separator />
                            </>
                        )}
                        <TimeUnit value={countdown.hours} label="HOURS" />
                        <Separator />
                        <TimeUnit value={countdown.minutes} label="MINUTES" />
                        <Separator />
                        <TimeUnit value={countdown.seconds} label="SECONDS" />
                    </Flex>

                    {countdown.days <= 30 && (
                        <Progress
                            value={progress}
                            size="xs"
                            borderRadius="full"
                            sx={{
                                '& > div': { background: DANGER },
                                background: `${DANGER}15`,
                            }}
                        />
                    )}
                </Box>
            </Flex>
        </Box>
    );
});

export default LimitedOfferAlert;